import {
  BALANCE_OPTIONS,
  HOLDER_AGE_OPTIONS,
  HOLDINGS_OPTIONS,
  LAST_OPTIONS,
  MATCH_OPTIONS,
  NAME_OPTIONS,
  SIGNALS_OPTIONS,
  TENURE_OPTIONS,
  TYPE_OPTIONS,
  type ColumnFilterOption,
} from './column-filter-options';
import { COLUMN_FILTER_KEYS, type ColumnFilters } from './ledger-filter';
import { EMPTY_COLUMN_FILTERS, hasActiveColumnFilters } from './ledger-filter';

/**
 * Summary of the active ledger column filters, with per-filter and clear-all
 * removal (SCRUM-867/977).
 *
 * This is NOT decoration. Once a header menu closes, the only evidence that a
 * filter is on would be a smaller row count — a user seeing "37 of 2,000" with no
 * visible reason reads it as missing data, which is precisely the "is it broken?"
 * reaction the reporter had to the old "Nightly data snapshot" label (SCRUM-897).
 * The chips make the filtered state legible and give one obvious way out.
 *
 * SCRUM-977: each column filter is now a single-select Ryan bucket, so a chip's
 * text is the SELECTED OPTION'S LABEL (e.g. "Under 30", "$10K – $50K"), prefixed
 * by the column. The label is looked up from the same option catalogue the menu
 * renders, so the chip and the menu can never disagree.
 */

/** Human column name for the chip prefix. */
const COLUMN_TITLE: Record<keyof ColumnFilters, string> = {
  name: 'Name',
  type: 'Type',
  tenure: 'Tenure',
  holderAge: 'Holder age',
  balance: 'Balance',
  holdings: 'Holdings',
  signals: 'Signals',
  match: 'Match',
  last: 'Last',
  branch: 'Branch',
  region: 'Region',
};

/** Fixed Ryan option catalogues, for label lookup by value. Branch/Region are
 * derived string values, so their chip label is the value itself. */
const FIXED_CATALOGUE: Partial<Record<keyof ColumnFilters, ColumnFilterOption<never>[]>> = {
  name: NAME_OPTIONS as ColumnFilterOption<never>[],
  type: TYPE_OPTIONS as ColumnFilterOption<never>[],
  tenure: TENURE_OPTIONS as ColumnFilterOption<never>[],
  holderAge: HOLDER_AGE_OPTIONS as ColumnFilterOption<never>[],
  balance: BALANCE_OPTIONS as ColumnFilterOption<never>[],
  holdings: HOLDINGS_OPTIONS as ColumnFilterOption<never>[],
  signals: SIGNALS_OPTIONS as ColumnFilterOption<never>[],
  match: MATCH_OPTIONS as ColumnFilterOption<never>[],
  last: LAST_OPTIONS as ColumnFilterOption<never>[],
};

/** The label to show for a column's currently-selected value. */
function optionLabel(
  key: keyof ColumnFilters,
  value: NonNullable<ColumnFilters[keyof ColumnFilters]>,
): string {
  const catalogue = FIXED_CATALOGUE[key];
  if (catalogue) {
    const hit = catalogue.find((o) => JSON.stringify(o.value) === JSON.stringify(value));
    if (hit) return hit.label;
  }
  // Branch/Region are the only catalogue-less columns; their value is the string
  // shown directly. Any bucket-valued column is always in FIXED_CATALOGUE above,
  // so a non-string here would be a wiring bug — guard rather than [object Object].
  return typeof value === 'string' ? value : JSON.stringify(value);
}

interface Chip {
  /** Stable key + the field this chip clears. */
  field: keyof ColumnFilters;
  label: string;
}

/** Builds one chip per active filter, iterating COLUMN_FILTER_KEYS (ledger order). */
function activeFilterChips(f: ColumnFilters): Chip[] {
  const chips: Chip[] = [];
  for (const key of COLUMN_FILTER_KEYS) {
    const value = f[key];
    if (value == null) continue;
    chips.push({ field: key, label: `${COLUMN_TITLE[key]}: ${optionLabel(key, value)}` });
  }
  return chips;
}

export function ActiveFiltersRow({
  filters,
  onChange,
}: {
  filters: ColumnFilters;
  onChange: (next: ColumnFilters) => void;
}) {
  if (!hasActiveColumnFilters(filters)) return null;

  const chips = activeFilterChips(filters);

  function clearField(field: keyof ColumnFilters) {
    onChange({ ...filters, [field]: EMPTY_COLUMN_FILTERS[field] });
  }

  return (
    <div
      data-testid="ledger-active-filters"
      className="gap-tk-sm flex flex-wrap items-center px-3 py-2"
    >
      <span className="text-text-3 font-mono text-[0.5625rem] tracking-[0.06em] uppercase">
        Filters
      </span>
      {chips.map((chip) => (
        <span
          key={chip.field}
          data-testid="ledger-filter-chip"
          className="rounded-tk-full border-border-1 bg-bg-3 text-text-1 inline-flex items-center gap-1.5 border px-2.5 py-0.5 text-[0.75rem]"
        >
          {chip.label}
          <button
            type="button"
            aria-label={`Remove ${chip.label} filter`}
            className="text-text-3 hover:text-text-0 cursor-pointer leading-none"
            onClick={() => {
              clearField(chip.field);
            }}
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        className="text-text-3 hover:text-text-0 ml-1 cursor-pointer text-[0.75rem] underline"
        onClick={() => {
          onChange(EMPTY_COLUMN_FILTERS);
        }}
      >
        Clear all
      </button>
    </div>
  );
}
